import * as React from "react"
import { z } from "zod"

import { useAuth } from "@/auth/useAuth"
import { ArrowRightIcon } from "lucide-react"
import { Button, FormBox } from "@/components/ui-kit"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { accountsApi } from "@/lib/accounts-api"
import { getApiErrorMessage } from "@/lib/error-codes"
import { useIsMobile } from "@/hooks/useIsMobile"
import { cn } from "@/lib/utils"

type AuthTab = "register" | "login"
type RegisterCustomerType = "person" | "business"

export type PersonalInfoSubmitResult =
  | { success: true; needsCustomerSelection: boolean }
  | { success: false }

export type PersonalInfoFormHandle = {
  submit: () => Promise<PersonalInfoSubmitResult>
}

type PersonalInfoFormProps = {
  onTabChange?: (tab: AuthTab) => void
  onLoginSubmit?: () => void
}

type RegisterFormState = {
  customerType: RegisterCustomerType
  email: string
  password: string
  confirmPassword: string
  fullName: string
  passport: string
  company: string
  inn: string
  phone: string
  personalDataConsent: boolean
  marketing: boolean
}

type RegisterFieldErrors = Partial<
  Record<
    | keyof RegisterFormState
    | "email"
    | "password"
    | "confirmPassword"
    | "fullName"
    | "passport"
    | "company"
    | "inn"
    | "phone"
    | "personalDataConsent",
    string
  >
>

const loginSchema = z.object({
  email: z.string().trim().min(1, "Введите почту"),
  password: z.string().min(1, "Введите пароль"),
})

const registerBaseSchema = z
  .object({
    email: z.string().trim().min(1, "Введите почту").email("Введите корректный email"),
    password: z.string().min(6, "Пароль должен быть не короче 6 символов"),
    confirmPassword: z.string().min(1, "Подтвердите пароль"),
    phone: z.string().trim().min(1, "Введите телефон"),
    personalDataConsent: z.boolean(),
    marketing: z.boolean(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Пароли не совпадают",
    path: ["confirmPassword"],
  })
  .refine((data) => data.personalDataConsent, {
    message: "Необходимо согласие на обработку персональных данных",
    path: ["personalDataConsent"],
  })

const registerPersonSchema = registerBaseSchema.extend({
  customerType: z.literal("person"),
  fullName: z.string().trim().min(1, "Введите ФИО"),
  passport: z.string().trim().min(1, "Введите серию и номер паспорта"),
})

const registerBusinessSchema = registerBaseSchema.extend({
  customerType: z.literal("business"),
  company: z.string().trim().min(1, "Введите название компании"),
  inn: z.string().trim().min(1, "Введите ИНН"),
})

const initialRegisterForm: RegisterFormState = {
  customerType: "person",
  email: "",
  password: "",
  confirmPassword: "",
  fullName: "",
  passport: "",
  company: "",
  inn: "",
  phone: "",
  personalDataConsent: false,
  marketing: false,
}

function collectZodErrors(error: z.ZodError): RegisterFieldErrors {
  const nextErrors: RegisterFieldErrors = {}

  for (const issue of error.issues) {
    const key = issue.path[0]
    if (typeof key === "string" && !(key in nextErrors)) {
      nextErrors[key as keyof RegisterFieldErrors] = issue.message
    }
  }

  return nextErrors
}

function FieldError({ message }: { message?: string }) {
  if (!message) {
    return null
  }

  return <p className="text-sm text-destructive">{message}</p>
}

function ConsentCheckbox({
  id,
  checked,
  label,
  onChange,
  error,
}: {
  id: string
  checked: boolean
  label: string
  onChange: (checked: boolean) => void
  error?: string
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex lg:items-start items-center gap-3">
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={(value) => onChange(value === true)}
          aria-invalid={error ? true : undefined}
        />
        <Label
          htmlFor={id}
          className="cursor-pointer lg:text-[18px] text-[14px] lg:leading-6 leading-[18px] font-normal text-light-muted peer-disabled:cursor-not-allowed"
        >
          {label}
        </Label>
      </div>
      <FieldError message={error} />
    </div>
  )
}

export const PersonalInfoForm = React.forwardRef<PersonalInfoFormHandle, PersonalInfoFormProps>(
  function PersonalInfoForm({ onTabChange, onLoginSubmit }, ref) {
    const isMobile = useIsMobile()
    const { login, register, isLoading } = useAuth()
    const [activeTab, setActiveTab] = React.useState<AuthTab>("register")
    const [loginForm, setLoginForm] = React.useState({ email: "", password: "" })
    const [registerForm, setRegisterForm] = React.useState<RegisterFormState>(initialRegisterForm)
    const [loginErrors, setLoginErrors] = React.useState<{ email?: string; password?: string }>({})
    const [registerErrors, setRegisterErrors] = React.useState<RegisterFieldErrors>({})
    const [submitError, setSubmitError] = React.useState<string | null>(null)
    const loginPasswordRef = React.useRef<HTMLInputElement>(null)

    const handleLoginEmailKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault()
        loginPasswordRef.current?.focus()
      }
    }

    const handleTabChange = (value: string) => {
      const tab = value as AuthTab
      setActiveTab(tab)
      setSubmitError(null)
      setLoginErrors({})
      setRegisterErrors({})
      onTabChange?.(tab)
    }

    const handleCustomerTypeChange = (value: string) => {
      const customerType = value as RegisterCustomerType
      setRegisterForm((prev) => ({ ...prev, customerType }))
      setRegisterErrors({})
    }

    const submitLogin = React.useCallback(async (): Promise<PersonalInfoSubmitResult> => {
      setSubmitError(null)
      setLoginErrors({})

      const parsed = loginSchema.safeParse(loginForm)
      if (!parsed.success) {
        const nextErrors: { email?: string; password?: string } = {}
        for (const issue of parsed.error.issues) {
          const key = issue.path[0]
          if (key === "email" || key === "password") {
            nextErrors[key] = issue.message
          }
        }
        setLoginErrors(nextErrors)
        return { success: false }
      }

      try {
        await login(parsed.data)
        return {
          success: true,
          needsCustomerSelection: accountsApi.getSelectedCustomerId() == null,
        }
      } catch (error) {
        setSubmitError(getApiErrorMessage(error))
        return { success: false }
      }
    }, [login, loginForm])

    const handleLoginSubmit = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (onLoginSubmit) {
        onLoginSubmit()
        return
      }
      void submitLogin()
    }

    const submitRegister = React.useCallback(async (): Promise<PersonalInfoSubmitResult> => {
      setSubmitError(null)
      setRegisterErrors({})

      const schema =
        registerForm.customerType === "person" ? registerPersonSchema : registerBusinessSchema
      const parsed = schema.safeParse(registerForm)

      if (!parsed.success) {
        setRegisterErrors(collectZodErrors(parsed.error))
        return { success: false }
      }

      try {
        if (parsed.data.customerType === "person") {
          await register({
            email: parsed.data.email,
            password: parsed.data.password,
            phone: parsed.data.phone,
            marketing: parsed.data.marketing,
            customerType: "person",
            name: parsed.data.fullName,
            passport: parsed.data.passport,
          })
        } else {
          await register({
            email: parsed.data.email,
            password: parsed.data.password,
            phone: parsed.data.phone,
            marketing: parsed.data.marketing,
            customerType: "business",
            name: parsed.data.company,
            company: parsed.data.company,
            vat: parsed.data.inn,
          })
        }

        return { success: true, needsCustomerSelection: false }
      } catch (error) {
        setSubmitError(getApiErrorMessage(error))
        return { success: false }
      }
    }, [register, registerForm])

    React.useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          if (activeTab === "login") {
            return submitLogin()
          }

          return submitRegister()
        },
      }),
      [activeTab, submitLogin, submitRegister],
    )

    // Rendered inside the active form: the tabs fill the card, so anything
    // placed after them lands at its bottom edge, away from the fields.
    const submitStatus = (
      <>
        {submitError ? (
          <p role="alert" className="text-sm text-destructive">
            {submitError}
          </p>
        ) : null}
        {isLoading ? <p className="text-sm text-light-muted">Отправляем данные...</p> : null}
      </>
    )

    return (
      <div className="flex flex-col px-2 py-6 lg:h-full lg:p-8 lg:pb-24">
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className={cn("flex flex-col gap-6 lg:h-full lg:gap-8")}
        >
          {activeTab === "register" ? (
            <Button
              variant="link"
              type="button"
              className="w-fit gap-2 px-0 py-0 text-[16px] leading-6 text-brand lg:text-[18px]"
              startIcon={<ArrowRightIcon size={isMobile ? 18 : 24} />}
              onClick={() => handleTabChange("login")}
            >
              Уже есть аккаунт? Войдите
            </Button>
          ) : (
            <Button
              variant="link"
              type="button"
              className="w-fit gap-2 px-0 py-0 text-[16px] leading-6 text-brand lg:text-[18px]"
              startIcon={<ArrowRightIcon size={isMobile ? 18 : 24} />}
              onClick={() => handleTabChange("register")}
            >
              Зарегистрироваться
            </Button>
          )}

          <TabsContent value="register" className="flex w-full flex-col lg:gap-3 gap-2 flex-none">
            <FormBox
              id="register-email"
              label="Почта"
              type="email"
              defaultValue={registerForm.email}
              onChange={(event) =>
                setRegisterForm((prev) => ({ ...prev, email: event.target.value }))
              }
              autoComplete="email"
            />
            <FieldError message={registerErrors.email} />

            <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-2 mb-[20px]">
              <div className="flex flex-col gap-1">
                <FormBox
                  id="register-password"
                  label="Пароль"
                  type="password"
                  defaultValue={registerForm.password}
                  onChange={(event) =>
                    setRegisterForm((prev) => ({ ...prev, password: event.target.value }))
                  }
                  autoComplete="new-password"
                />
                <FieldError message={registerErrors.password} />
              </div>

              <div className="flex flex-col gap-1">
                <FormBox
                  id="register-confirm-password"
                  label="Подтверждение пароля"
                  type="password"
                  defaultValue={registerForm.confirmPassword}
                  onChange={(event) =>
                    setRegisterForm((prev) => ({ ...prev, confirmPassword: event.target.value }))
                  }
                  autoComplete="new-password"
                />
                <FieldError message={registerErrors.confirmPassword} />
              </div>
            </div>

            <Tabs
              value={registerForm.customerType}
              onValueChange={handleCustomerTypeChange}
              className="flex w-full flex-col gap-4 flex-none"
            >
              <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-2 lg:mb-[32px] mb-4">
                <TabsList
                  variant="segment"
                  className="grid w-full grid-cols-2 self-start rounded-lg bg-background p-1"
                >
                  <TabsTrigger
                    value="person"
                    className="lg:text-[18px] text-[12px] leading-[24px] data-active:!text-brand cursor-pointer"
                  >
                    Физическое лицо
                  </TabsTrigger>
                  <TabsTrigger
                    value="business"
                    className="lg:text-[18px] text-[12px] leading-[24px] data-active:!text-brand cursor-pointer"
                  >
                    Юридическое лицо
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="person" className="flex flex-col lg:gap-4 gap-2 flex-none">
                <FormBox
                  id="register-full-name"
                  label="ФИО"
                  defaultValue={registerForm.fullName}
                  onChange={(event) =>
                    setRegisterForm((prev) => ({ ...prev, fullName: event.target.value }))
                  }
                  autoComplete="name"
                />
                <FieldError message={registerErrors.fullName} />

                <FormBox
                  id="register-passport"
                  label="Серия и номер паспорта"
                  defaultValue={registerForm.passport}
                  onChange={(event) =>
                    setRegisterForm((prev) => ({ ...prev, passport: event.target.value }))
                  }
                  autoComplete="off"
                />
                <FieldError message={registerErrors.passport} />

                <FormBox
                  id="register-person-phone"
                  label="Телефон"
                  type="tel"
                  defaultValue={registerForm.phone}
                  onChange={(event) =>
                    setRegisterForm((prev) => ({ ...prev, phone: event.target.value }))
                  }
                  autoComplete="tel"
                />
                <FieldError message={registerErrors.phone} />

                <ConsentCheckbox
                  id="register-person-personal-data"
                  checked={registerForm.personalDataConsent}
                  label="Согласен на обработку персональных данных"
                  onChange={(checked) =>
                    setRegisterForm((prev) => ({ ...prev, personalDataConsent: checked }))
                  }
                  error={registerErrors.personalDataConsent}
                />

                <ConsentCheckbox
                  id="register-person-marketing"
                  checked={registerForm.marketing}
                  label="Согласен на получение новостей и акций"
                  onChange={(checked) =>
                    setRegisterForm((prev) => ({ ...prev, marketing: checked }))
                  }
                />
              </TabsContent>

              <TabsContent value="business" className="flex flex-col gap-4 flex-none">
                <FormBox
                  id="register-company"
                  label="Название компании"
                  defaultValue={registerForm.company}
                  onChange={(event) =>
                    setRegisterForm((prev) => ({ ...prev, company: event.target.value }))
                  }
                  autoComplete="organization"
                />
                <FieldError message={registerErrors.company} />

                <FormBox
                  id="register-inn"
                  label="ИНН"
                  defaultValue={registerForm.inn}
                  onChange={(event) =>
                    setRegisterForm((prev) => ({ ...prev, inn: event.target.value }))
                  }
                  autoComplete="off"
                />
                <FieldError message={registerErrors.inn} />

                <FormBox
                  id="register-business-phone"
                  label="Телефон"
                  type="tel"
                  defaultValue={registerForm.phone}
                  onChange={(event) =>
                    setRegisterForm((prev) => ({ ...prev, phone: event.target.value }))
                  }
                  autoComplete="tel"
                />
                <FieldError message={registerErrors.phone} />

                <ConsentCheckbox
                  id="register-business-personal-data"
                  checked={registerForm.personalDataConsent}
                  label="Согласен на обработку персональных данных"
                  onChange={(checked) =>
                    setRegisterForm((prev) => ({ ...prev, personalDataConsent: checked }))
                  }
                  error={registerErrors.personalDataConsent}
                />

                <ConsentCheckbox
                  id="register-business-marketing"
                  checked={registerForm.marketing}
                  label="Согласен на получение новостей и акций"
                  onChange={(checked) =>
                    setRegisterForm((prev) => ({ ...prev, marketing: checked }))
                  }
                />
              </TabsContent>
            </Tabs>

            {submitStatus}
          </TabsContent>

          <TabsContent value="login" className="flex flex-col gap-4 flex-none">
            <form onSubmit={handleLoginSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <FormBox
                  id="login-email"
                  label="Почта"
                  type="email"
                  defaultValue={loginForm.email}
                  onChange={(event) =>
                    setLoginForm((prev) => ({ ...prev, email: event.target.value }))
                  }
                  autoComplete="username"
                  enterKeyHint="next"
                  onKeyDown={handleLoginEmailKeyDown}
                  aria-invalid={loginErrors.email ? true : undefined}
                />
                <FieldError message={loginErrors.email} />
              </div>

              <div className="flex flex-col gap-1">
                <FormBox
                  ref={loginPasswordRef}
                  id="login-password"
                  label="Пароль"
                  type="password"
                  defaultValue={loginForm.password}
                  onChange={(event) =>
                    setLoginForm((prev) => ({ ...prev, password: event.target.value }))
                  }
                  autoComplete="current-password"
                  enterKeyHint="go"
                  aria-invalid={loginErrors.password ? true : undefined}
                />
                <FieldError message={loginErrors.password} />
              </div>

              {submitStatus}
            </form>
          </TabsContent>
        </Tabs>
      </div>
    )
  },
)
